import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Shield, Wrench, X } from 'lucide-react';
import { mfaApi } from '../services/authApi';

export const SafetyBanner: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [showMfa, setShowMfa] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    let cancelled = false;
    mfaApi.getStatus().then((status) => {
      if (!cancelled && !status.mfaEnabled && !status.setupDismissedAt) {
        setShowMfa(true);
      }
    }).catch(() => {
      // Silently ignore — MFA line just won't show
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/error-pages/announcement.json')
      .then(res => {
        if (!res.ok) throw new Error('No announcement');
        return res.json();
      })
      .then(data => {
        if (!cancelled && data.message) {
          setAnnouncement(data.message);
        }
      })
      .catch(() => {
        // No announcement to show
      });
    return () => { cancelled = true; };
  }, []);

  const handleDismiss = async () => {
    setIsVisible(false);
    if (showMfa) {
      try {
        await mfaApi.dismissBanner();
      } catch {
        // Best-effort
      }
    }
  };

  if (!isVisible && !announcement) return null;

  return (
    <div
      className="bg-amber-50 dark:bg-orange-950 border-b border-amber-200 dark:border-orange-900 px-3 py-1.5"
      data-testid="safety-banner"
    >
      <div className="flex items-center justify-center gap-3 mx-auto">
        <div className="flex items-center text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mr-1.5" />
          <span className="text-amber-800 dark:text-medical-300">
            ClinLexis assists with evidence navigation. It does not provide medical or legal opinions.
          </span>
          {showMfa && (
            <>
              <span className="text-amber-300 dark:text-medical-600 mx-2.5 select-none" aria-hidden="true">|</span>
              <Shield className="h-3.5 w-3.5 text-primary-600 dark:text-primary-500 flex-shrink-0 mr-1.5" />
              <span className="text-amber-800 dark:text-medical-300 whitespace-nowrap">
                Protect your account with two-step verification.
              </span>
              <button
                onClick={() => navigate('/settings/mfa')}
                className="ml-1 font-medium text-primary-700 dark:text-primary-500 hover:text-primary-900 dark:hover:text-primary-100 underline whitespace-nowrap"
              >
                Set up now
              </button>
            </>
          )}
          {announcement && (
            <>
              <span className="text-amber-300 dark:text-medical-600 mx-2.5 select-none" aria-hidden="true">|</span>
              <Wrench className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mr-1.5" />
              <span className="text-amber-800 dark:text-medical-300">
                {announcement}
              </span>
            </>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="text-amber-600 dark:text-medical-400 hover:text-amber-800 dark:hover:text-medical-300 transition-colors flex-shrink-0"
          aria-label="Dismiss banner"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};
